/**
 * ScriptBlockEditor — mini inline editor for a single ScriptBlock.
 * Opened by clicking the block's text. There are no Save / Cancel buttons:
 * clicking anywhere outside the editor saves (see `useCommitOnClickAway`),
 * and Escape discards the edit.
 *
 * Round 4:
 *  - The DIALOGUE / SOUND EFFECT kind label is now a segmented-pill toggle
 *    with a ⇄ icon, cursor-pointer affordance, and tooltip so it reads as
 *    an interactive switch rather than a static badge.
 *  - Toggling DIALOGUE → SOUND EFFECT preserves the character + emotion
 *    values in component state (item 8). Only the input UI is hidden when
 *    kind === 'sfx'. Toggling back to DIALOGUE restores the previous values.
 *    The serializer (`serializeSpeechBlock`) only emits character/emotion in
 *    the dialogue form, so SFX serialization stays clean.
 *  - The body uses the shared RichTextBox (chips + @mention flyout) so
 *    typing `@` opens the same mention dropdown that Freeform mode shows
 *    (items 7 + 10).
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { focusEditableAtEnd, useCommitOnClickAway } from './useCommitOnClickAway'
import { useTranslation } from 'react-i18next'
import type { ScriptBlock, SegmentKind } from '../scriptParser'
import { RichTextBox, type RichTextBoxHandle, type RichTextBoxState } from '../../shared/RichTextBox'
import type { ReactFlowCanvasNode } from '../../../../../hooks/useYCanvas'

interface ScriptBlockEditorProps {
  block: ScriptBlock
  onSave: (updated: ScriptBlock) => void
  onCancel: () => void
  /**
   * Fires when the user clicks the kind label to toggle dialogue ↔ sfx.
   * Parent uses this to emit the `block-kind-toggle` NAD trace event.
   * The new kind is reflected immediately in local state so the editor
   * re-renders with the right fields; the change is only persisted on Save.
   */
  onKindToggle?: (blockId: string, from: SegmentKind, to: SegmentKind) => void
  /** Canvas nodes for the @mention flyout (item 10). */
  canvasNodes?: ReactFlowCanvasNode[]
  /**
   * Imperative handle for the body RichTextBox. Forwarded up so the
   * CINEMATOGRAPHY PRESETS panel can target the editor's caret in
   * Structured mode (item 4).
   */
  bodyRef?: React.RefObject<RichTextBoxHandle>
  /**
   * True for a freshly added block that is not in the prompt yet. Clicking
   * away discards it when its body is empty instead of saving a blank block.
   */
  isDraft?: boolean
}

export const ScriptBlockEditor = memo(function ScriptBlockEditor({
  block,
  onSave,
  onCancel,
  onKindToggle,
  canvasNodes,
  bodyRef,
  isDraft = false,
}: ScriptBlockEditorProps) {
  const { t } = useTranslation('canvas')
  const [text, setText] = useState(block.text)
  // Item 8: keep character + emotion in state even when kind flips to sfx,
  // so toggling back to dialogue restores them.
  const [character, setCharacter] = useState(block.character ?? '')
  const [emotion, setEmotion] = useState(block.emotion ?? '')
  const [startSec, setStartSec] = useState(block.startSec.toFixed(1))
  const [endSec, setEndSec] = useState(block.endSec.toFixed(1))
  const [kind, setKind] = useState<SegmentKind>(block.kind)

  // Fallback ref so RichTextBox always has a target even when the parent
  // doesn't pass `bodyRef`.
  const localBodyRef = useRef<RichTextBoxHandle>(null)
  const effectiveBodyRef = bodyRef ?? localBodyRef

  const isAudio = kind === 'dialogue' || kind === 'voiceover' || kind === 'sfx'
  const isSpeech = kind === 'dialogue' || kind === 'voiceover'
  const isSfx = kind === 'sfx'
  // Only audio blocks expose the dialogue ↔ sfx toggle. Video blocks keep
  // their existing read-only kind label.
  const canToggleKind = isAudio

  const handleToggleKind = useCallback(() => {
    if (!canToggleKind) return
    // Voiceover blocks toggle to sfx as well (and back to dialogue), which
    // matches user intent: "this isn't a spoken line, it's a sound effect."
    const next: SegmentKind = isSfx ? 'dialogue' : 'sfx'
    const from = kind
    setKind(next)
    // NB (round 4 item 8): do NOT clear character/emotion. The serializer
    // skips them for sfx; toggling back to dialogue must restore them.
    onKindToggle?.(block.id, from, next)
  }, [canToggleKind, isSfx, kind, block.id, onKindToggle])

  const buildNext = useCallback((): ScriptBlock => {
    const s = parseFloat(startSec)
    const e = parseFloat(endSec)
    // `camera` is preserved on the block (passthrough from the existing raw
    // text). The user no longer edits this field directly — the editor UI
    // does not expose it as of round 2.
    return {
      ...block,
      kind,
      startSec: Number.isFinite(s) ? s : block.startSec,
      endSec: Number.isFinite(e) && e > s ? e : block.endSec,
      text: text.trim(),
      // Item 8: round-trip character + emotion even when current kind is
      // sfx. Serializer drops them for sfx anyway, but in-memory they are
      // preserved so a follow-up sfx→dialogue toggle restores them.
      character: character.trim() || undefined,
      emotion: emotion.trim() || undefined,
    }
  }, [block, kind, text, character, emotion, startSec, endSec])

  // Click-away commit. An untouched block just closes (no rewrite of the
  // prompt); an empty new block is discarded rather than saved blank.
  const rootRef = useRef<HTMLDivElement>(null)
  const handleClickAway = useCallback(() => {
    const next = buildNext()
    if (isDraft) {
      if (next.text) onSave(next)
      else onCancel()
      return
    }
    const unchanged =
      next.kind === block.kind &&
      next.startSec === block.startSec &&
      next.endSec === block.endSec &&
      next.text === block.text.trim() &&
      (next.character ?? '') === (block.character ?? '').trim() &&
      (next.emotion ?? '') === (block.emotion ?? '').trim()
    if (unchanged) onCancel()
    else onSave(next)
  }, [buildNext, isDraft, block, onSave, onCancel])
  useCommitOnClickAway(rootRef, handleClickAway)

  // Opened by a single click on the block text: start typing immediately.
  useEffect(() => {
    focusEditableAtEnd(rootRef.current)
  }, [])

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onCancel()
      }
    },
    [onCancel],
  )

  const kindLabel = isSfx ? 'SOUND EFFECT' : kind.toUpperCase()

  return (
    // Fills its timeline slot (h-full) rather than growing it: the body takes
    // the remaining height and scrolls internally, so opening an editor never
    // makes the lane — and the whole motion prompt — taller.
    <div
      ref={rootRef}
      className="h-full flex flex-col gap-1.5 bg-surface-2 rounded-lg p-2 border border-primary/40 min-w-0 w-full overflow-hidden"
      data-testid="block-editor"
      data-block-kind={kind}
    >
      <div className="flex gap-1 items-center min-w-0 shrink-0">
        <input
          type="number"
          step={0.1}
          min={0}
          value={startSec}
          onChange={(e) => setStartSec(e.target.value)}
          onKeyDown={handleInputKeyDown}
          className="w-12 bg-input-bg border border-input-border rounded px-1 py-0.5 text-xs font-medium"
          data-testid="block-editor-start"
          placeholder={t('scriptBlockEditor.placeholderStart')}
        />
        <span className="text-xxs text-text-muted">→</span>
        <input
          type="number"
          step={0.1}
          min={0}
          value={endSec}
          onChange={(e) => setEndSec(e.target.value)}
          onKeyDown={handleInputKeyDown}
          className="w-12 bg-input-bg border border-input-border rounded px-1 py-0.5 text-xs font-medium"
          data-testid="block-editor-end"
          placeholder={t('scriptBlockEditor.placeholderEnd')}
        />
        {canToggleKind ? (
          // Item 1: clearly-interactive segmented pill with ⇄ icon, hover
          // state, and a tooltip explaining the toggle target.
          <button
            type="button"
            onClick={handleToggleKind}
            className={`ml-2 inline-flex items-center gap-1 text-xxs uppercase tracking-wider rounded-full px-2 py-0.5 cursor-pointer transition-colors border ${
              isSfx
                ? 'border-purple-400/50 bg-purple-500/15 text-purple-300 hover:bg-purple-500/25 hover:border-purple-400'
                : 'border-emerald-400/50 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 hover:border-emerald-400'
            }`}
            data-testid="block-editor-kind-toggle"
            data-kind={kind}
            title={`Click to switch to ${isSfx ? 'DIALOGUE' : 'SOUND EFFECT'}`}
          >
            <span className="truncate">{kindLabel}</span>
            {/* ⇄ icon — communicates "switchable" at a glance. */}
            <svg
              className="w-2.5 h-2.5 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7 16h13M7 16l3 3M7 16l3-3M17 8H4M17 8l-3-3M17 8l-3 3"
              />
            </svg>
          </button>
        ) : (
          <span
            className="text-xxs uppercase tracking-wider text-text-muted ml-2 truncate"
            data-testid="block-editor-kind"
          >
            {kindLabel}
          </span>
        )}
      </div>
      {isSpeech && (
        <div className="flex gap-1 min-w-0 shrink-0">
          <input
            type="text"
            value={character}
            onChange={(e) => setCharacter(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={t('scriptBlockEditor.character')}
            className="flex-1 min-w-0 bg-input-bg border border-input-border rounded px-1.5 py-0.5 text-xs font-medium box-border"
            data-testid="block-editor-character"
          />
          <input
            type="text"
            value={emotion}
            onChange={(e) => setEmotion(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={t('scriptBlockEditor.emotionPlaceholder')}
            className="flex-1 min-w-0 max-w-[40%] bg-input-bg border border-input-border rounded px-1.5 py-0.5 text-xs font-medium box-border"
            data-testid="block-editor-emotion"
          />
        </div>
      )}
      {/* Item 7 + 10: body is the shared RichTextBox so @mention chips
          render and the mention flyout works the same as Freeform mode. */}
      <div className="relative flex-1 min-h-[48px]">
        <div className="absolute inset-0 flex flex-col">
          <RichTextBox
            ref={effectiveBodyRef}
            flexFill
            canvasNodes={canvasNodes}
            initialContent={text}
            onContentChange={(state: RichTextBoxState) => setText(state.plainText)}
            placeholder={
              isSpeech
                ? 'Dialogue text'
                : isSfx
                  ? 'Describe the sound effect'
                  : 'Describe action'
            }
            containerClassName="w-full min-w-0 bg-input-bg border border-input-border rounded px-1.5 py-1 nowheel"
            containerTestId="block-editor-text"
            editorTestId="block-editor-text-editor"
            // Keep trackpad scroll inside the body instead of chaining to
            // the sideways-scrolling timeline.
            className="overscroll-contain"
            maxLength={2000}
            onEscape={onCancel}
          />
        </div>
      </div>
    </div>
  )
})
